import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Check, X, Lightbulb, Send, HelpCircle, MessageCircle, Loader2 } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { logActivity, getWrongQuestionsHistory, supabase, saveAiFeedback } from '../lib/supabase';
import { FillInQuestion } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { MathKeyboard } from './MathKeyboard';
import { callClaude } from '../lib/claude';
import { getCourseProgress, getNextRecommendation, Recommendation } from '../lib/recommendations';
import { RecommendationCard } from './RecommendationCard';
import { DeutschFillInQuestion } from '../data/deutsch-schularbeit/fill-in-questions';

// Shuffle array helper
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Smart question selection with wrong-answer priority and rotation
function selectSmartQuestions<T extends { id: string }>(
  questions: T[],
  wrongHistory: string[],
  lastQuizQuestionIds: string[],
  count: number
): T[] {
  const questionWeights = questions.map(q => {
    const wrongCount = wrongHistory.filter(id => id === q.id).length;
    const wasInLastQuiz = lastQuizQuestionIds.includes(q.id);

    let weight = 1;
    if (wrongCount >= 3) weight = 4;
    else if (wrongCount >= 2) weight = 3;
    else if (wrongCount === 1) weight = 2;

    if (wasInLastQuiz) weight = Math.max(0.5, weight - 2);
    if (wrongCount === 0 && wasInLastQuiz) weight = 0.3;

    return { question: q, weight };
  });

  const selected: T[] = [];
  const remaining = [...questionWeights];

  while (selected.length < count && remaining.length > 0) {
    const totalWeight = remaining.reduce((sum, qw) => sum + qw.weight, 0);
    let random = Math.random() * totalWeight;
    let selectedIndex = 0;

    for (let i = 0; i < remaining.length; i++) {
      random -= remaining[i].weight;
      if (random <= 0) {
        selectedIndex = i;
        break;
      }
    }

    selected.push(remaining[selectedIndex].question);
    remaining.splice(selectedIndex, 1);
  }

  return shuffleArray(selected);
}

// Web Audio API Sound Effects
const audioContext = typeof window !== 'undefined'
  ? new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
  : null;

function playSound(type: 'correct' | 'wrong') {
  if (!audioContext) return;
  if (audioContext.state === 'suspended') audioContext.resume();

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  const now = audioContext.currentTime;

  if (type === 'correct') {
    oscillator.frequency.setValueAtTime(600, now);
    oscillator.frequency.setValueAtTime(800, now + 0.05);
    gainNode.gain.setValueAtTime(0.15, now);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
    oscillator.start(now);
    oscillator.stop(now + 0.1);
  } else {
    oscillator.frequency.setValueAtTime(300, now);
    gainNode.gain.setValueAtTime(0.1, now);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
    oscillator.start(now);
    oscillator.stop(now + 0.1);
  }
}

// Check if question is a Deutsch extended question
function isDeutschQuestion(q: FillInQuestion): q is DeutschFillInQuestion {
  return 'linkedLesson' in q && 'explanation' in q;
}

// Parse AI response for indirect speech
function parseIndirectSpeechResponse(text: string): { points: number; maxPoints: number; feedback: string } {
  // Format: "PUNKTE: X/2 | FEEDBACK: ..."
  const pointsMatch = text.match(/PUNKTE:\s*(\d+)\s*\/\s*(\d+)/i);
  const feedbackMatch = text.match(/FEEDBACK:\s*(.+?)(?:$)/is);

  return {
    points: pointsMatch ? parseInt(pointsMatch[1], 10) : 0,
    maxPoints: pointsMatch ? parseInt(pointsMatch[2], 10) : 2,
    feedback: feedbackMatch ? feedbackMatch[1].trim() : text,
  };
}

export function FillInQuiz() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const linkedLessonId = searchParams.get('lesson'); // For lesson-linked mode
  const categoryFilter = searchParams.get('category');

  const { course, isLoading, error } = useCourse(courseId || '');
  const { user } = useAuth();

  const [quizKey, setQuizKey] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [activeGapIndex, setActiveGapIndex] = useState(0);
  const [isChecked, setIsChecked] = useState(false);
  const [results, setResults] = useState<{
    correct: boolean;
    answers: string[];
    expected: string[];
    questionId: string;
    points: number;
    maxPoints: number;
  }[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [startTime] = useState(Date.now());
  const [wrongHistory, setWrongHistory] = useState<string[]>([]);
  const [lastQuizIds, setLastQuizIds] = useState<string[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);

  // New states for KI-Hilfe-Button
  const [attempts, setAttempts] = useState(0);
  const [hintVisible, setHintVisible] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [hintTimerExpired, setHintTimerExpired] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // States for indirect speech (API)
  const [isSubmittingApi, setIsSubmittingApi] = useState(false);
  const [apiFeedback, setApiFeedback] = useState<string | null>(null);
  const [apiPoints, setApiPoints] = useState<{ points: number; maxPoints: number } | null>(null);

  // Frag Claude AI chat
  const [showAiChat, setShowAiChat] = useState(false);
  const [aiMessages, setAiMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const aiMessagesEndRef = useRef<HTMLDivElement>(null);
  const aiInputRef = useRef<HTMLInputElement>(null);

  // Check if this is a course with extended FillIn features (linkedLesson, explanation, points)
  const isDeutschCourse = courseId === 'deutsch-schularbeit' || courseId === 'mathe-schularbeit';

  // Load wrong question history
  useEffect(() => {
    async function loadHistory() {
      if (!user?.id || !courseId) {
        setHistoryLoaded(true);
        return;
      }

      const history = await getWrongQuestionsHistory(user.id, courseId, 'fill-in');
      setWrongHistory(history.wrongHistory);
      setLastQuizIds(history.lastQuizQuestionIds);
      setHistoryLoaded(true);
    }

    loadHistory();
  }, [user?.id, courseId, quizKey]);

  // Get questions using smart selection (or filtered by lesson)
  const questions = useMemo<FillInQuestion[]>(() => {
    if (!course?.fillInQuestions || !historyLoaded) return [];

    let questionsToUse = course.fillInQuestions;

    // If linked to a specific lesson, filter questions
    if (linkedLessonId && isDeutschCourse) {
      questionsToUse = course.fillInQuestions.filter(q =>
        isDeutschQuestion(q) && q.linkedLesson === linkedLessonId
      );
      // Return all questions for the lesson (no smart selection)
      return questionsToUse;
    }

    // If filtered by category, use only questions from that category
    if (categoryFilter) {
      questionsToUse = questionsToUse.filter(q => q.category === categoryFilter);
      return selectSmartQuestions(questionsToUse, wrongHistory, lastQuizIds, Math.min(10, questionsToUse.length));
    }

    const pflicht = questionsToUse.filter(q => q.difficulty === 'pflicht');
    return selectSmartQuestions(pflicht, wrongHistory, lastQuizIds, 10);
  }, [course?.fillInQuestions, wrongHistory, lastQuizIds, historyLoaded, linkedLessonId, isDeutschCourse, categoryFilter]);

  const currentQuestion = questions[currentIndex];
  const isIndirectSpeech = currentQuestion && isDeutschQuestion(currentQuestion) && currentQuestion.type === 'indirect_speech';

  // Reset state when moving to a NEW question (not on retry)
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setHintTimerExpired(false);
    setHintVisible(false);
    setShowExplanation(false);
    setAttempts(0);
    setApiFeedback(null);
    setApiPoints(null);
    setIsChecked(false);

    // Start 10-second timer for hint visibility (Deutsch course only)
    if (isDeutschCourse && currentQuestion) {
      timerRef.current = setTimeout(() => {
        setHintTimerExpired(true);
      }, 10000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // Only reset when question changes, NOT on isChecked change (retry)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, quizKey]);

  // Parse sentence to find gaps
  const parseSentence = (sentence: string) => {
    const parts = sentence.split('_____');
    return parts;
  };

  // Call Claude API for indirect speech
  const callClaudeAPI = useCallback(async (question: DeutschFillInQuestion, userAnswer: string): Promise<string> => {
    const prompt = question.apiPrompt?.replace('{userAnswer}', userAnswer) || '';

    const data = await callClaude({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 300,
      messages: [{ role: 'user', content: prompt }],
      system: 'Du bist ein freundlicher Deutsch-Tutor für Adriana (14, 4. Klasse MS Wien). Bewerte ihre Umwandlung in die indirekte Rede. Sei ermutigend aber ehrlich. Antworte im Format: PUNKTE: X/2 | FEEDBACK: [dein Feedback]',
    }, 'fillin-transform-check');

    return data.content[0]?.text || '';
  }, []);

  // AI fallback check for fill-in gaps
  const checkGapWithAI = async (
    question: FillInQuestion,
    gapIndex: number,
    userAnswer: string
  ): Promise<{ result: 'correct' | 'almost' | 'wrong'; explanation?: string }> => {
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const gap = question.gaps[gapIndex];
      const response = await fetch(`${supabaseUrl}/functions/v1/check-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: question.sentence,
          expectedAnswer: gap.acceptedAnswers.join(', '),
          userAnswer,
          instruction: question.instruction,
        }),
      });
      const data = await response.json();
      if (data.fallback || data.error) return { result: 'wrong' };
      return { result: data.result, explanation: data.explanation };
    } catch {
      return { result: 'wrong' };
    }
  };

  // Check if all answers are correct (for local checking)
  const checkAnswersLocal = async () => {
    if (!currentQuestion) return;

    const expected = currentQuestion.gaps.map(gap => gap.acceptedAnswers[0]);

    // First pass: check each gap with strict string matching
    const gapResults = currentQuestion.gaps.map((gap, index) => {
      const userAnswer = (userAnswers[index] || '').trim().toLowerCase();
      const strictMatch = gap.acceptedAnswers.some(accepted =>
        accepted.toLowerCase() === userAnswer
      );
      return { gapIndex: index, strictMatch, userAnswer };
    });

    // Find gaps that failed strict matching but have a non-empty answer
    const failedGaps = gapResults.filter(g => !g.strictMatch && g.userAnswer.length >= 2);

    let allCorrect = gapResults.every(g => g.strictMatch);

    // If there are failed gaps, ask AI for second opinion
    if (failedGaps.length > 0 && !allCorrect) {
      setIsSubmittingApi(true);
      try {
        const aiResults = await Promise.all(
          failedGaps.map(g => checkGapWithAI(currentQuestion, g.gapIndex, g.userAnswer))
        );

        // Update results: if AI says correct/almost, count as correct
        failedGaps.forEach((g, i) => {
          if (aiResults[i].result === 'correct' || aiResults[i].result === 'almost') {
            gapResults[g.gapIndex].strictMatch = true;
          }
        });

        allCorrect = gapResults.every(g => g.strictMatch);
      } catch {
        // AI failed, keep strict matching results
      } finally {
        setIsSubmittingApi(false);
      }
    }

    playSound(allCorrect ? 'correct' : 'wrong');

    // Calculate points based on attempts (Deutsch course)
    let earnedPoints = 0;
    const maxPoints = 2;
    if (isDeutschCourse) {
      if (allCorrect) {
        if (attempts === 0) {
          earnedPoints = 2; // First try
        } else if (attempts === 1) {
          earnedPoints = 1; // After hint
        } else {
          earnedPoints = 0; // After explanation
        }
      }
    } else {
      earnedPoints = allCorrect ? 1 : 0;
    }

    setIsChecked(true);

    // Show explanation if wrong (Deutsch course)
    if (!allCorrect && isDeutschCourse && isDeutschQuestion(currentQuestion)) {
      if (attempts >= 1) {
        setShowExplanation(true);
      }
    }

    setResults(prev => [...prev, {
      correct: allCorrect,
      answers: userAnswers,
      expected,
      questionId: currentQuestion.id,
      points: earnedPoints,
      maxPoints: isDeutschCourse ? maxPoints : 1,
    }]);
  };

  // Check answers for indirect speech (API)
  const checkAnswersApi = async () => {
    if (!currentQuestion || !isDeutschQuestion(currentQuestion) || !userAnswers[0]) return;

    setIsSubmittingApi(true);

    try {
      const response = await callClaudeAPI(currentQuestion, userAnswers[0]);
      const parsed = parseIndirectSpeechResponse(response);

      setApiFeedback(parsed.feedback);
      setApiPoints({ points: parsed.points, maxPoints: parsed.maxPoints });

      playSound(parsed.points >= 1 ? 'correct' : 'wrong');

      setIsChecked(true);
      setResults(prev => [...prev, {
        correct: parsed.points >= 1,
        answers: userAnswers,
        expected: [currentQuestion.expectedAnswer || ''],
        questionId: currentQuestion.id,
        points: parsed.points,
        maxPoints: parsed.maxPoints,
      }]);

      // Save AI feedback
      if (user?.id) {
        await saveAiFeedback(user.id, 'fill_in_quiz', response, { feedback: parsed.feedback }, {
          courseId: courseId || '',
          question: currentQuestion.directSpeech || '',
          userAnswer: userAnswers[0],
          scoreInfo: `${parsed.points}/${parsed.maxPoints}`,
        });
      }
    } catch (err) {
      console.error('API Error:', err);
      // Fallback to showing expected answer
      setApiFeedback('Fehler bei der KI-Bewertung. Hier ist die erwartete Antwort:');
      setApiPoints({ points: 0, maxPoints: 2 });
      setIsChecked(true);
    } finally {
      setIsSubmittingApi(false);
    }
  };

  // Main check function
  const checkAnswers = () => {
    if (!currentQuestion) return;

    setAttempts(prev => prev + 1);

    if (isIndirectSpeech) {
      checkAnswersApi();
    } else {
      checkAnswersLocal();
    }
  };

  // Try again (for wrong answers in Deutsch course)
  // Reset AI chat on question change
  useEffect(() => {
    setShowAiChat(false);
    setAiMessages([]);
    setAiInput('');
  }, [currentIndex]);

  useEffect(() => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  const handleAiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const question = aiInput.trim();
    if (!question || aiLoading || !currentQuestion) return;

    setAiMessages(prev => [...prev, { role: 'user', content: question }]);
    setAiInput('');
    setAiLoading(true);

    try {
      const courseLabel = courseId === 'mathe-schularbeit' ? 'Mathematik (4. Klasse MS)' : courseId === 'deutsch-schularbeit' ? 'Deutsch (4. Klasse MS)' : 'Englisch (4. Klasse MS)';
      const systemPrompt = `Du hilfst einer Schülerin bei einer Lückentext-Aufgabe (${courseLabel}).
Die aktuelle Aufgabe: "${currentQuestion.instruction}"
Der Satz: "${currentQuestion.sentence}"
Erkläre in einfacher Sprache. Hilf den Lösungsweg zu finden, gib NICHT direkt die Antwort.
Wenn die Schülerin nach der Lösung fragt, gib einen Hinweis statt der direkten Antwort.
Antworte auf Deutsch, kurz und verständlich.`;

      const apiMessages = [
        ...aiMessages.map(m => ({ role: m.role, content: m.content })),
        { role: 'user' as const, content: question },
      ];

      const data = await callClaude({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 500,
        system: systemPrompt,
        messages: apiMessages,
      }, 'fillin-followup');

      const assistantText = data.content?.[0]?.text || 'Entschuldigung, es gab einen Fehler.';
      setAiMessages(prev => [...prev, { role: 'assistant', content: assistantText }]);

      // Log AI interaction
      if (user?.id) {
        await saveAiFeedback(
          user.id,
          'fill_in_quiz',
          assistantText,
          { question_id: currentQuestion.id, instruction: currentQuestion.instruction },
          {
            courseId: courseId || '',
            question,
            userAnswer: userAnswers.join(', '),
          }
        );
        await logActivity(user.id, 'fill_in_ai_help', {
          course_id: courseId,
          question_id: currentQuestion.id,
          question,
        });
      }
    } catch (error) {
      console.error('Claude API error:', error);
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler. Bitte versuche es nochmal.',
      }]);
    } finally {
      setAiLoading(false);
      aiInputRef.current?.focus();
    }
  };

  const tryAgain = () => {
    setIsChecked(false);
    setUserAnswers([]);
    setHintVisible(true); // Show hint after first wrong attempt
    // Remove the last result since we're retrying
    setResults(prev => prev.slice(0, -1));
  };

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(i => i + 1);
      setUserAnswers([]);
      setIsChecked(false);
      setShowHint(false);
      setHintVisible(false);
      setShowExplanation(false);
      setApiFeedback(null);
      setApiPoints(null);
    } else {
      // Show loading spinner
      setIsSaving(true);

      // Calculate final score
      const totalPoints = results.reduce((sum, r) => sum + r.points, 0);
      const totalMaxPoints = results.reduce((sum, r) => sum + r.maxPoints, 0);
      const percentage = totalMaxPoints > 0 ? Math.round((totalPoints / totalMaxPoints) * 100) : 0;
      const elapsedSeconds = Math.round((Date.now() - startTime) / 1000);

      // Collect wrong question IDs
      const wrongQuestions = results
        .filter(r => !r.correct)
        .map(r => r.questionId);

      // Collect all question IDs for this quiz
      const allQuestionIds = questions.map(q => q.id);

      // Determine effective linked lesson for Deutsch course
      // Even in general mode, if all questions belong to one lesson, use that lesson's ID
      let effectiveLinkedLessonId = linkedLessonId;
      if (isDeutschCourse && !linkedLessonId) {
        const lessonIds = new Set(
          questions
            .filter(isDeutschQuestion)
            .map(q => q.linkedLesson)
        );
        // If all questions belong to a single lesson, use that lesson
        if (lessonIds.size === 1) {
          effectiveLinkedLessonId = Array.from(lessonIds)[0];
        }
      }

      if (user?.id && courseId) {
        // Save to quiz_results for smart question selection
        await supabase.from('quiz_results').insert({
          user_id: user.id,
          course_id: courseId,
          mode: effectiveLinkedLessonId ? `fill-in-${effectiveLinkedLessonId}` : 'fill-in',
          score: totalPoints,
          total: totalMaxPoints,
          percentage: percentage,
          wrong_questions: wrongQuestions,
          question_ids: allQuestionIds,
          time_seconds: elapsedSeconds,
        });

        // Log activity
        await logActivity(user.id, 'fill_in', {
          course_id: courseId,
          score: totalPoints,
          total: totalMaxPoints,
          percentage: percentage,
          category: currentQuestion?.category || 'general',
          duration_minutes: Math.ceil(elapsedSeconds / 60),
          linked_lesson: effectiveLinkedLessonId || null,
        });

        // Load recommendation for result screen
        if (course) {
          const progress = await getCourseProgress(user.id, courseId, course);
          const rec = getNextRecommendation(courseId, progress, course, {
            justCompleted: 'fill-in',
            lastScore: percentage,
          });
          setRecommendation(rec);
        }
      }
      setIsSaving(false);
      setIsFinished(true);
    }
  };

  const restart = () => {
    setQuizKey(k => k + 1);
    setCurrentIndex(0);
    setUserAnswers([]);
    setIsChecked(false);
    setResults([]);
    setIsFinished(false);
    setShowHint(false);
    setHintVisible(false);
    setShowExplanation(false);
    setAttempts(0);
    setApiFeedback(null);
    setApiPoints(null);
  };

  if (isLoading || !course) {
    return <div className="loading">Laden...</div>;
  }

  if (isSaving) {
    return <LoadingSpinner text="Ergebnis wird gespeichert..." />;
  }

  if (isSubmittingApi) {
    return <LoadingSpinner text="KI bewertet..." />;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Keine Lückentext-Fragen verfügbar.</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  // Finished screen
  if (isFinished) {
    const totalPoints = results.reduce((sum, r) => sum + r.points, 0);
    const totalMaxPoints = results.reduce((sum, r) => sum + r.maxPoints, 0);
    const percentage = totalMaxPoints > 0 ? Math.round((totalPoints / totalMaxPoints) * 100) : 0;

    return (
      <div style={{ padding: '16px', paddingBottom: '100px', textAlign: 'center' }}>
        <h1 style={{ color: '#fff', fontSize: '28px', marginBottom: '8px' }}>
          Fertig!
        </h1>
        <div style={{
          fontSize: '48px',
          fontWeight: 700,
          color: percentage >= 60 ? '#4caf50' : '#ff9800',
          marginBottom: '8px',
        }}>
          {totalPoints}/{totalMaxPoints}
        </div>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '32px' }}>
          {percentage}% richtig
          {linkedLessonId && percentage >= 60 && (
            <span style={{ display: 'block', marginTop: '8px', color: '#4caf50' }}>
              Nächste Lektion freigeschaltet!
            </span>
          )}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={restart}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px 24px',
              background: '#ffd54f',
              border: 'none',
              borderRadius: '12px',
              color: '#0a0e27',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <RotateCcw size={20} />
            Nochmal
          </button>
          <Link
            to={`/course/${courseId}`}
            style={{
              padding: '16px 24px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '12px',
              color: '#e8eaf6',
              fontSize: '16px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Zurück zum Kurs
          </Link>
        </div>

        {/* Recommendation */}
        {recommendation && (
          <RecommendationCard recommendation={recommendation} />
        )}
      </div>
    );
  }

  const sentenceParts = parseSentence(currentQuestion.sentence);
  const deutschQuestion = isDeutschQuestion(currentQuestion) ? currentQuestion : null;
  const canShowHint = isDeutschCourse && (hintTimerExpired || attempts > 0 || hintVisible);

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      <Link
        to={linkedLessonId ? `/course/${courseId}/text-lessons` : `/course/${courseId}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          textDecoration: 'none',
          marginBottom: '20px',
        }}
      >
        <ArrowLeft size={18} />
        Zurück
      </Link>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <span style={{
          background: 'rgba(255, 213, 79, 0.2)',
          color: '#ffd54f',
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: 600,
        }}>
          Frage {currentIndex + 1} / {questions.length}
        </span>
        {isDeutschCourse && (
          <span style={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: '13px',
          }}>
            {attempts === 0 ? '2 Punkte möglich' : attempts === 1 ? '1 Punkt möglich' : '0 Punkte'}
          </span>
        )}
      </div>

      {/* Instruction */}
      <div style={{
        background: 'rgba(255, 213, 79, 0.1)',
        borderRadius: '12px',
        padding: '14px 16px',
        marginBottom: '20px',
        fontSize: '15px',
        fontWeight: 600,
        color: '#ffd54f',
      }}>
        {currentQuestion.instruction}
      </div>

      {/* Direct Speech for indirect_speech type */}
      {isIndirectSpeech && deutschQuestion?.directSpeech && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <div style={{ color: '#9fa8da', fontSize: '12px', marginBottom: '8px' }}>
            Originaltext:
          </div>
          <div style={{
            color: '#fff',
            fontSize: '17px',
            fontWeight: 600,
            fontStyle: 'italic',
            lineHeight: '1.5',
          }}>
            {deutschQuestion.directSpeech}
          </div>
        </div>
      )}

      {/* Sentence with gaps (for non-indirect_speech) */}
      {!isIndirectSpeech && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
        }}>
          <div style={{
            fontSize: '17px',
            lineHeight: '2.2',
            color: '#e8eaf6',
          }}>
            {sentenceParts.map((part, index) => (
              <React.Fragment key={index}>
                <span>{part}</span>
                {index < sentenceParts.length - 1 && (
                  <span style={{ display: 'inline-block', margin: '0 4px' }}>
                    {isChecked ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 12px',
                        borderRadius: '8px',
                        background: currentQuestion.gaps[index].acceptedAnswers.some(
                          a => a.toLowerCase() === (userAnswers[index] || '').trim().toLowerCase()
                        ) ? 'rgba(76, 175, 80, 0.2)' : 'rgba(239, 83, 80, 0.2)',
                        border: `2px solid ${
                          currentQuestion.gaps[index].acceptedAnswers.some(
                            a => a.toLowerCase() === (userAnswers[index] || '').trim().toLowerCase()
                          ) ? '#4caf50' : '#ef5350'
                        }`,
                      }}>
                        {currentQuestion.gaps[index].acceptedAnswers.some(
                          a => a.toLowerCase() === (userAnswers[index] || '').trim().toLowerCase()
                        ) ? (
                          <>
                            <Check size={16} style={{ color: '#4caf50' }} />
                            <span style={{ color: '#4caf50', fontWeight: 600 }}>
                              {userAnswers[index] || '—'}
                            </span>
                          </>
                        ) : (
                          <>
                            <X size={16} style={{ color: '#ef5350' }} />
                            <span style={{ color: '#ef5350', textDecoration: 'line-through' }}>
                              {userAnswers[index] || '—'}
                            </span>
                            <span style={{ color: '#4caf50', fontWeight: 600, marginLeft: '8px' }}>
                              {currentQuestion.gaps[index].acceptedAnswers[0]}
                            </span>
                          </>
                        )}
                      </span>
                    ) : (
                      <input
                        type="text"
                        value={userAnswers[index] || ''}
                        onChange={(e) => {
                          const newAnswers = [...userAnswers];
                          newAnswers[index] = e.target.value;
                          setUserAnswers(newAnswers);
                        }}
                        placeholder="..."
                        style={{
                          width: '120px',
                          padding: '8px 12px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '2px solid rgba(255, 213, 79, 0.5)',
                          borderRadius: '8px',
                          color: '#ffd54f',
                          fontSize: '16px',
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          textAlign: 'center',
                        }}
                        autoFocus={index === 0}
                        onFocus={() => setActiveGapIndex(index)}
                      />
                    )}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Textarea for indirect_speech */}
      {isIndirectSpeech && (
        <textarea
          value={userAnswers[0] || ''}
          onChange={(e) => setUserAnswers([e.target.value])}
          placeholder="Schreibe den Satz in indirekter Rede..."
          disabled={isChecked}
          style={{
            width: '100%',
            minHeight: '100px',
            padding: '14px',
            background: isChecked ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '12px',
            color: '#e8eaf6',
            fontSize: '16px',
            fontFamily: 'inherit',
            resize: 'vertical',
            marginBottom: '16px',
          }}
        />
      )}

      {/* KI-Hilfe-Button (Deutsch course only) */}
      {isDeutschCourse && !isChecked && currentQuestion.hint && (
        <button
          onClick={() => setShowHint(!showHint)}
          disabled={!canShowHint}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: canShowHint ? '#ff9800' : 'rgba(255, 152, 0, 0.3)',
            fontSize: '14px',
            cursor: canShowHint ? 'pointer' : 'default',
            marginBottom: '16px',
            padding: 0,
            opacity: canShowHint ? 1 : 0.5,
            transition: 'opacity 0.3s ease',
          }}
        >
          <HelpCircle size={18} />
          {showHint ? 'Hinweis verstecken' : 'Hilfe anzeigen'}
        </button>
      )}

      {/* Hint (always visible for English, conditional for Deutsch) */}
      {!isDeutschCourse && currentQuestion.hint && !isChecked && (
        <button
          onClick={() => setShowHint(!showHint)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#ff9800',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '16px',
            padding: 0,
          }}
        >
          <Lightbulb size={18} />
          {showHint ? 'Hinweis verstecken' : 'Hinweis anzeigen'}
        </button>
      )}

      {showHint && currentQuestion.hint && !isChecked && (
        <div style={{
          background: 'rgba(255, 152, 0, 0.1)',
          border: '1px solid rgba(255, 152, 0, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#ff9800',
        }}>
          {currentQuestion.hint}
        </div>
      )}

      {/* Explanation after checking (Deutsch course) */}
      {isChecked && deutschQuestion?.explanation && (showExplanation || !results[results.length - 1]?.correct) && (
        <div style={{
          background: 'rgba(76, 175, 80, 0.1)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#4caf50',
        }}>
          <div style={{ fontWeight: 700, marginBottom: '6px' }}>Erklärung:</div>
          {deutschQuestion.explanation}
        </div>
      )}

      {/* API Feedback for indirect speech */}
      {isChecked && apiFeedback && (
        <div style={{
          background: 'rgba(66, 165, 245, 0.1)',
          border: '1px solid rgba(66, 165, 245, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '12px',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
            <span style={{ color: '#42a5f5', fontWeight: 700, fontSize: '14px' }}>
              KI-Feedback
            </span>
            {apiPoints && (
              <span style={{ color: '#ffd54f', fontWeight: 700, fontSize: '14px' }}>
                {apiPoints.points}/{apiPoints.maxPoints} Punkte
              </span>
            )}
          </div>
          <div style={{ color: '#e8eaf6', fontSize: '14px', lineHeight: '1.6' }}>
            {apiFeedback}
          </div>
        </div>
      )}

      {/* Expected answer for indirect speech */}
      {isChecked && isIndirectSpeech && deutschQuestion?.expectedAnswer && (
        <div style={{
          background: 'rgba(76, 175, 80, 0.1)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '20px',
        }}>
          <div style={{ color: '#4caf50', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
            Erwartete Antwort:
          </div>
          <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
            {deutschQuestion.expectedAnswer}
          </div>
        </div>
      )}

      {/* Math Keyboard for Mathe course */}
      {isDeutschCourse && courseId === 'mathe-schularbeit' && !isChecked && (
        <MathKeyboard
          onInsert={(symbol) => {
            const newAnswers = [...userAnswers];
            newAnswers[activeGapIndex] = (newAnswers[activeGapIndex] || '') + symbol;
            setUserAnswers(newAnswers);
          }}
          onTransfer={(value) => {
            const newAnswers = [...userAnswers];
            newAnswers[activeGapIndex] = value;
            setUserAnswers(newAnswers);
          }}
        />
      )}

      {/* Frag Claude button */}
      {!showAiChat && (
        <button
          onClick={() => { setShowAiChat(true); setTimeout(() => aiInputRef.current?.focus(), 100); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
            padding: '10px', marginTop: '12px',
            background: 'rgba(124, 77, 255, 0.08)',
            border: '1px solid rgba(124, 77, 255, 0.2)',
            borderRadius: '10px',
            color: '#b388ff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <MessageCircle size={16} /> Frag Claude — ich erklär dir das!
        </button>
      )}

      {/* AI Chat Panel */}
      {showAiChat && (
        <div style={{
          marginTop: '12px',
          background: 'rgba(124, 77, 255, 0.08)',
          border: '1px solid rgba(124, 77, 255, 0.25)',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', background: 'rgba(124, 77, 255, 0.1)',
            borderBottom: '1px solid rgba(124, 77, 255, 0.15)',
          }}>
            <span style={{ color: '#b388ff', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MessageCircle size={14} /> Frag Claude
            </span>
            <button onClick={() => setShowAiChat(false)} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '4px', display: 'flex',
            }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '10px 14px' }}>
            {aiMessages.length === 0 && (
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>
                Stell deine Frage zu dieser Aufgabe...
              </div>
            )}
            {aiMessages.map((msg, i) => (
              <div key={i} style={{ marginBottom: '10px', textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                <div style={{
                  display: 'inline-block', maxWidth: '85%', padding: '8px 12px',
                  borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  background: msg.role === 'user' ? 'rgba(124, 77, 255, 0.2)' : 'rgba(255, 255, 255, 0.06)',
                  color: '#e8eaf6', fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap', textAlign: 'left',
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
            {aiLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#b388ff', fontSize: '13px' }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Claude denkt nach...
              </div>
            )}
            <div ref={aiMessagesEndRef} />
          </div>
          <form onSubmit={handleAiSubmit} style={{
            display: 'flex', gap: '8px', padding: '10px 14px',
            borderTop: '1px solid rgba(124, 77, 255, 0.15)',
          }}>
            <input
              ref={aiInputRef}
              type="text" value={aiInput} onChange={(e) => setAiInput(e.target.value)}
              placeholder="Was verstehst du nicht?" disabled={aiLoading}
              style={{
                flex: 1, padding: '10px 12px', background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(124, 77, 255, 0.2)', borderRadius: '8px',
                color: '#e8eaf6', fontSize: '14px', minHeight: '44px',
              }}
            />
            <button type="submit" disabled={aiLoading || !aiInput.trim()} style={{
              padding: '10px 14px', background: 'rgba(124, 77, 255, 0.25)',
              border: '1px solid rgba(124, 77, 255, 0.3)', borderRadius: '8px',
              color: '#b388ff', cursor: 'pointer', display: 'flex', alignItems: 'center',
              opacity: aiLoading || !aiInput.trim() ? 0.4 : 1,
            }}>
              <Send size={16} />
            </button>
          </form>
        </div>
      )}

      {/* Actions */}
      <div style={{ marginTop: '24px' }}>
        {!isChecked ? (
          <button
            onClick={checkAnswers}
            disabled={isIndirectSpeech
              ? !userAnswers[0]?.trim()
              : userAnswers.filter(a => a?.trim()).length < currentQuestion.gaps.length
            }
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: (isIndirectSpeech ? userAnswers[0]?.trim() : userAnswers.filter(a => a?.trim()).length >= currentQuestion.gaps.length)
                ? '#ffd54f'
                : 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '12px',
              color: (isIndirectSpeech ? userAnswers[0]?.trim() : userAnswers.filter(a => a?.trim()).length >= currentQuestion.gaps.length)
                ? '#0a0e27'
                : 'rgba(255, 255, 255, 0.3)',
              fontSize: '16px',
              fontWeight: 700,
              cursor: (isIndirectSpeech ? userAnswers[0]?.trim() : userAnswers.filter(a => a?.trim()).length >= currentQuestion.gaps.length)
                ? 'pointer'
                : 'default',
            }}
          >
            {isIndirectSpeech && <Send size={20} />}
            Prüfen
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Retry button for wrong answers (Deutsch course, max 2 attempts, not for indirect_speech) */}
            {isDeutschCourse && !results[results.length - 1]?.correct && attempts < 2 && !isIndirectSpeech && (
              <button
                onClick={tryAgain}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'rgba(255, 152, 0, 0.2)',
                  border: '1px solid #ff9800',
                  borderRadius: '12px',
                  color: '#ff9800',
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Nochmal versuchen ({2 - attempts} {attempts === 1 ? 'Versuch' : 'Versuche'} übrig)
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                width: '100%',
                padding: '16px',
                background: '#ffd54f',
                border: 'none',
                borderRadius: '12px',
                color: '#0a0e27',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {currentIndex < questions.length - 1 ? 'Weiter' : 'Ergebnis anzeigen'}
            </button>
          </div>
        )}
      </div>

      {/* Score */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '20px',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px',
      }}>
        <span style={{ color: '#4caf50' }}>
          {results.reduce((sum, r) => sum + r.points, 0)} Punkte
        </span>
        <span style={{ color: 'rgba(255,255,255,0.4)' }}>
          von {results.reduce((sum, r) => sum + r.maxPoints, 0)} möglich
        </span>
      </div>
    </div>
  );
}
